import { BrowserRouter } from "react-router-dom";
import { Layout } from "./components/layout/layout";
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import {
  AuthConfig,
  AuthGuard,
  ClientType,
  CookieBanner,
  IDPType,
  MaintenanceWindow,
  useDynaTraceAnalyticsHandler,
  useMaintenanceConfig,
  useMaintenanceWindow,
} from "@fce-bees/fce-common-components";
import { useEffect, useState } from "react";
import i18n from "./i18n";
import { ThemeName, ThemeProvider } from "@ucl/shared-component-foundations";
import { MaintenancePage } from "src/pages/maintenance-page/maintenance-page";
import { useCookiesPoll } from "./hooks/use-cookie-poll";
import { CaseProvider } from "./context/case-context";
import { AppRoutes } from "./routes/routes";
import { ScrollToTop } from "./components/scroll-to-top/scroll-to-top";
import DevTools from "src/mocks/dev-tools/dev-tools";
import { Flags } from "src/consts/flags";
import { useObjectFlag } from "src/consts/flagWrapper";

const authConfig: AuthConfig = {
  idpClientId: import.meta.env.VITE_TENANT_ID,
  baseAuthorizationURL: "",
  redirectURI: `${import.meta.env.VITE_IMS_BASE_URL}${import.meta.env.VITE_IMS_AAD_EXCHANGE_URL}`,
  responseMode: "form_post",
};

function AppContent() {
  const [authError, setAuthError] = useState(false);
  const [cookiePolling] = useState(false);
  const cookiePreferences = useCookiesPoll(cookiePolling);
  const appName: string = "mfcro";
  const [maintenanceWindow, setMaintenanceWindow] = useState<MaintenanceWindow | null>(null);

  const maintenanceConfig = useMaintenanceConfig(
    appName,
    i18n.language,
    useObjectFlag(Flags.PLANNED_MAINTENANCE),
    useObjectFlag(Flags.SCHEDULED_MAINTENANCE)
  );

  const hasMaintenanceConfig = maintenanceConfig?.planned != null || maintenanceConfig?.scheduled != null;
  useMaintenanceWindow(maintenanceConfig, maintenanceWindow, appName, i18n.language, setMaintenanceWindow);
  useEffect(() => {
    if (!hasMaintenanceConfig) {
      setMaintenanceWindow(null);
    }
  }, [hasMaintenanceConfig]);

  useDynaTraceAnalyticsHandler({
    src: import.meta.env.VITE_MFC_DYNATRACE_SCRIPT_SRC,
    trackCookies: cookiePreferences,
  });

  const applicationId = import.meta.env.VITE_APPLICATION_ID;
  const imsBaseUrl = import.meta.env.VITE_IMS_BASE_URL;

  const handleAuthError = () => {
    setAuthError(true);
  };

  return (
    <CaseProvider>
      <Layout>
        {maintenanceWindow || authError ? (
          <MaintenancePage />
        ) : (
          <AuthGuard
            {...{ applicationId, imsBaseUrl }}
            authType="COOKIE"
            cookieDomain=".ford.com"
            onError={handleAuthError}
            idpType={IDPType.guest}
            clientType={ClientType.guest}
            authConfig={authConfig}
            market="en-GB"
            applicationBasePath={`${window.location.origin}`}
          >
            <CookieBanner region={"uk"} />
            <ScrollToTop />
            <AppRoutes />

            <DevTools />
          </AuthGuard>
        )}
      </Layout>
    </CaseProvider>
  );
}

export default function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <HelmetProvider>
        <ThemeProvider themeName={ThemeName.DSE} productTeamName="Motor Finance Commission Redress" disableAnalytics>
          <BrowserRouter basename="/">
            <AppContent />
          </BrowserRouter>
        </ThemeProvider>
      </HelmetProvider>
    </I18nextProvider>
  );
}
